import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, X } from "lucide-react";
import { api, formatNum } from "@/lib/api";
import { saveBlob } from "./bankUtils";

const TITLES = { maas: "Banka Talimatı (Maaş)", prim: "Prim Talimatı" };

export const BankFileModal = ({ yil, ay, kind, onClose }) => {
  const [data, setData] = useState(null);
  const [desc, setDesc] = useState({});
  const [excl, setExcl] = useState(new Set());
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/payroll/bank-file/preview?yil=${yil}&ay=${ay}&kind=${kind}`)
      .then((r) => {
        if (!alive) return;
        setData(r.data);
        setDesc(Object.fromEntries(r.data.rows.map((x) => [x.personnel_id, x.aciklama])));
        setExcl(new Set(r.data.rows.filter((x) => x.excluded && !x.elden).map((x) => x.personnel_id)));
      })
      .catch(() => { if (alive) { toast.error("Önizleme yüklenemedi"); onClose(); } });
    return () => { alive = false; };
  }, [yil, ay, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const included = useMemo(() => (data?.rows || []).filter((r) => !excl.has(r.personnel_id) && !r.elden), [data, excl]);
  const total = included.reduce((s, r) => s + r.tutar, 0);
  const toggle = (id) => setExcl((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const applyAll = () => { if (bulk.trim()) setDesc(Object.fromEntries(Object.keys(desc).map((k) => [k, bulk.slice(0, 40)]))); };

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payroll/bank-file", { yil, ay, kind, descriptions: desc, excluded: [...excl] }, { responseType: "blob" });
      saveBlob(r, `denizbank_${kind}.xlsx`);
      const miss = Number(r.headers["x-missing-iban"] || 0);
      if (miss) toast.warning(`${miss} kişinin IBAN'ı yok · dosyadaki "Eksik IBAN" sayfasına bakın`);
      else toast.success("Talimat dosyası indirildi ve geçmişe kaydedildi");
      onClose();
    } catch { toast.error("Dosya oluşturulamadı"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="bank-file-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">{TITLES[kind]} · Açıklamaları Düzenle</div>
            <div className="text-[11px] text-slate-500">Açıklama en fazla 40 karakter · kesintiler (ücretsiz izin, rapor, avans, ek kesinti) otomatik eklenir (mavi) · elle değiştirilenler sarı</div>
          </div>
          <button onClick={onClose} data-testid="bank-file-close" className="w-7 h-7 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={14} /></button>
        </div>
        {!data ? (
          <div className="p-8 text-center text-slate-400 text-sm"><Loader2 size={16} className="inline animate-spin mr-1" /> Yükleniyor...</div>
        ) : (
          <>
            {data.sent_file && (
              <div className="mx-3 mt-3 text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-300 rounded p-2" data-testid="bank-file-sent-warning">
                Bu ayın {kind === "maas" ? "maaş" : "prim"} talimatı <strong>{new Date(data.sent_file.sent_at).toLocaleString("tr-TR")}</strong> tarihinde {data.sent_file.sent_by_name} tarafından
                bankaya gönderildi olarak işaretlendi ({data.sent_file.count} ödeme · {formatNum(data.sent_file.total)} ₺). Yeni indireceğiniz dosya taslak olarak kaydedilir.
              </div>
            )}
            <div className="px-3 pt-3 flex items-center gap-2 flex-wrap">
              <input value={bulk} maxLength={40} onChange={(e) => setBulk(e.target.value)} placeholder={data.default_desc}
                data-testid="bank-desc-bulk" className="flex-1 min-w-[220px] px-2 py-1.5 border border-slate-300 rounded-md text-sm" />
              <button onClick={applyAll} data-testid="bank-desc-apply-all" className="px-3 py-1.5 bg-slate-900 text-white rounded-md text-xs font-semibold">Tümüne Uygula</button>
              <button onClick={() => setDesc(Object.fromEntries(data.rows.map((x) => [x.personnel_id, x.auto_desc])))} data-testid="bank-desc-reset"
                className="px-3 py-1.5 border border-slate-300 rounded-md text-xs font-semibold">Varsayılana Dön</button>
            </div>
            <div className="p-3 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5 w-6" title="İşareti kaldırılan kişi bu ayın dosyasına girmez"></th>
                    <th className="text-left px-2 py-1.5">Alıcı</th><th className="text-left px-2 py-1.5">IBAN</th>
                    <th className="text-right px-2 py-1.5">Tutar</th><th className="text-left px-2 py-1.5">Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.personnel_id} className={`border-b border-slate-100 ${excl.has(r.personnel_id) || r.elden ? "opacity-50 bg-slate-50" : ""}`}>
                      <td className="px-2 py-1">
                        <input type="checkbox" checked={!excl.has(r.personnel_id) && !r.elden} disabled={r.elden}
                          title={r.elden ? "Elden ödenecek olarak işaretli (Bordro'dan değiştirin)" : ""}
                          onChange={() => toggle(r.personnel_id)} data-testid={`bank-include-${r.sicil_no}`} />
                      </td>
                      <td className="px-2 py-1 font-semibold text-slate-900">{r.ad_soyad}{r.elden && <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-violet-100 text-violet-800">ELDEN</span>}</td>
                      <td className="px-2 py-1 font-mono text-[11px] text-slate-600">{r.iban}</td>
                      <td className="px-2 py-1 text-right font-mono-tab">{formatNum(r.tutar)}</td>
                      <td className="px-2 py-1">
                        <input value={desc[r.personnel_id] || ""} maxLength={40} data-testid={`bank-desc-${r.sicil_no}`}
                          onChange={(e) => setDesc({ ...desc, [r.personnel_id]: e.target.value })}
                          title={desc[r.personnel_id] !== r.auto_desc ? `Elle değiştirildi · otomatik: ${r.auto_desc}` : (r.auto_desc !== data.default_desc ? "Kesintiler otomatik eklendi" : "")}
                          className={`w-full px-2 py-1 border rounded text-xs ${desc[r.personnel_id] !== r.auto_desc ? "border-amber-400 bg-amber-50" : r.auto_desc !== data.default_desc ? "border-sky-300 bg-sky-50" : "border-slate-300"}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.pending_salary?.length > 0 && (
                <div className="mt-3 text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded p-2" data-testid="bank-pending-salary">
                  Maaşı henüz girilmediği için dosyaya girmeyecek (Maaş bekleniyor): {data.pending_salary.map((m) => m.ad_soyad).join(", ")}
                </div>
              )}
              {data.missing.length > 0 && (
                <div className="mt-3 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2" data-testid="bank-missing-iban">
                  IBAN'ı olmadığı için dosyaya girmeyecek: {data.missing.map((m) => m.ad_soyad).join(", ")}
                </div>
              )}
            </div>
            <div className="p-3 border-t flex items-center justify-between gap-2 bg-slate-50 rounded-b-xl">
              <div className="text-xs text-slate-600" data-testid="bank-file-summary">{included.length} ödeme{data.rows.length - included.length ? ` (${data.rows.length - included.length} hariç)` : ""} · <strong>{formatNum(total)} ₺</strong></div>
              <div className="flex gap-2">
                <button onClick={onClose} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-semibold">İptal</button>
                <button onClick={download} disabled={busy || !included.length} data-testid="bank-file-download"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-700 hover:bg-sky-800 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel İndir
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
